import React from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { usePolling, getPollingQueryOpts } from "../layout/HoaAdminLayout"
import { toast } from "sonner"
import {
    getHOAAdminProfile,
    updateHOAAdminProfile,
    getPaymentDetails,
    createPaymentDetails,
    updatePaymentDetails,
    changePassword,
    type PaymentDetails,
    type HOAAdminProfile,
    type UpdatePaymentDetailsData,
} from "@/api/settings"
import {
  listBuildings,
  getBuildingPaymentConfig,
  updateBuildingPaymentConfig,
  type BuildingList,
} from "@/api/buildings"
import {
    listAvailablePlans,
    getMySubscription,
    listMyInvoices,
    requestSubscriptionPlan,
    uploadSubscriptionProof,
    cancelSubscriptionInvoice,
    getPlatformPaymentInfo,
    type PublicSubscriptionPlan,
    type HOASubscription as MyHOASubscription,
    type SubscriptionInvoice,
    type SubscriptionPaymentMethod,
    type PlatformPaymentInfo,
} from "@/api/subscriptions"
import { useLanguage } from "@/contexts/LanguageContext"
import { forceUpdate, getServiceWorkerState } from "@/utils/sw-registration"
import { PhoneInputField } from "@/components/ui/phone-input"

/* ================= Types ================= */
type NotificationPrefs = {
  email: boolean
  sms: boolean
  maintenanceAlerts: boolean
  paymentReminders: boolean
}

type SecurityAccess = {
  requerirFotosProblemas: boolean
  autoAprobarResidentes: boolean
  permitirAccesoInvitados: boolean
}

/* ============== Mock API for non-essential sections ============== */
function sleep(ms: number) { return new Promise(r => setTimeout(r, ms)) }

let MOCK_PREFS = {
  notifications: {
    email: true,
    sms: false,
    maintenanceAlerts: true,
    paymentReminders: true,
  },
  security: {
    requerirFotosProblemas: true,
    autoAprobarResidentes: false,
    permitirAccesoInvitados: false,
  },
}

async function apiGetNotifications(): Promise<NotificationPrefs> {
  await sleep(120)
  return JSON.parse(JSON.stringify(MOCK_PREFS.notifications))
}
async function apiUpdateNotifications(data: NotificationPrefs): Promise<NotificationPrefs> {
  await sleep(200)
  MOCK_PREFS.notifications = data
  return JSON.parse(JSON.stringify(MOCK_PREFS.notifications))
}
async function apiGetSecurity(): Promise<SecurityAccess> {
  await sleep(120)
  return JSON.parse(JSON.stringify(MOCK_PREFS.security))
}
async function apiUpdateSecurity(data: SecurityAccess): Promise<SecurityAccess> {
  await sleep(200)
  MOCK_PREFS.security = data
  return JSON.parse(JSON.stringify(MOCK_PREFS.security))
}


/* ================= Tokens ================= */
const PRIMARY = "#1F3A93"
/** Quiet outline style that stays black-on-white */
const QUIET =
  "!bg-white !text-gray-900 !border-black/10 border ring-1 ring-black/5 shadow-sm rounded-xl hover:!bg-gray-50 active:!bg-gray-50"

/* ================= Page ================= */
export default function SettingsPage() {
  const { t, language } = useLanguage()
  const locale = language === "es" ? "es-VE" : "en-US"

  const { enabled, intervalMs } = usePolling()
  const qc = useQueryClient()
  const [selectedBuildingId, setSelectedBuildingId] = React.useState<string>("")

  // Fetch HOA admin profile and payment details
  const profileQuery = useQuery({
    queryKey: ["hoa-admin", "profile"],
    queryFn: getHOAAdminProfile,
    ...getPollingQueryOpts(enabled, intervalMs),
  })

  const paymentDetailsQuery = useQuery({
    queryKey: ["hoa-admin", "payment-details"],
    queryFn: getPaymentDetails,
    ...getPollingQueryOpts(enabled, intervalMs),
  })

  const buildingsQuery = useQuery({
    queryKey: ["hoa-admin", "buildings"],
    queryFn: listBuildings,
    ...getPollingQueryOpts(enabled, intervalMs),
  })

  React.useEffect(() => {
    if (!selectedBuildingId && buildingsQuery.data?.length) {
      setSelectedBuildingId(buildingsQuery.data[0].id)
    }
  }, [selectedBuildingId, buildingsQuery.data])

  const buildingPaymentConfigQuery = useQuery({
    queryKey: ["hoa-admin", "building-payment-config", selectedBuildingId],
    queryFn: () => getBuildingPaymentConfig(selectedBuildingId),
    enabled: !!selectedBuildingId,
    ...getPollingQueryOpts(enabled, intervalMs),
  })

  const notifQuery = useQuery({
    queryKey: ["hoa-admin", "notifications"],
    queryFn: apiGetNotifications,
    ...getPollingQueryOpts(enabled, intervalMs),
  })

  const secQuery = useQuery({
    queryKey: ["hoa-admin", "security"],
    queryFn: apiGetSecurity,
    ...getPollingQueryOpts(enabled, intervalMs),
  })

  // Mutations
  const updateProfileMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { first_name?: string; last_name?: string; organization_name?: string } }) =>
      updateHOAAdminProfile(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hoa-admin", "profile"] })
      toast.success(t("settings.profileUpdated"))
    },
    onError: () => toast.error(t("settings.profileUpdateError")),
  })

  const createPaymentMut = useMutation({
    mutationFn: (data: UpdatePaymentDetailsData) => createPaymentDetails(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hoa-admin", "payment-details"] })
      toast.success(t("settings.paymentDetailsCreated"))
    },
    onError: () => toast.error(t("settings.paymentDetailsCreateError")),
  })

  const updatePaymentMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePaymentDetailsData }) =>
      updatePaymentDetails(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hoa-admin", "payment-details"] })
      toast.success(t("settings.paymentDetailsUpdated"))
    },
    onError: () => toast.error(t("settings.paymentDetailsUpdateError")),
  })

  const updateBuildingPaymentMut = useMutation({
    mutationFn: ({ buildingId, data }: { buildingId: string; data: UpdatePaymentDetailsData }) =>
      updateBuildingPaymentConfig(buildingId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hoa-admin", "buildings"] })
      qc.invalidateQueries({ queryKey: ["hoa-admin", "building-payment-config", selectedBuildingId] })
      toast.success("Building payment details updated")
    },
    onError: () => toast.error("Error updating building payment details"),
  })

  const updateNotifMut = useMutation({
    mutationFn: apiUpdateNotifications,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hoa-admin", "notifications"] })
      toast.success(t("settings.notificationsUpdated"))
    },
    onError: () => toast.error(t("settings.notificationsUpdateError")),
  })

  const updateSecMut = useMutation({
    mutationFn: apiUpdateSecurity,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hoa-admin", "security"] })
      toast.success(t("settings.securityUpdated"))
    },
    onError: () => toast.error(t("settings.securityUpdateError")),
  })

  const isLoading =
    profileQuery.isLoading ||
    paymentDetailsQuery.isLoading ||
    buildingsQuery.isLoading ||
    (selectedBuildingId ? buildingPaymentConfigQuery.isLoading : false) ||
    notifQuery.isLoading ||
    secQuery.isLoading
  const isFetching =
    profileQuery.isFetching ||
    paymentDetailsQuery.isFetching ||
    buildingsQuery.isFetching ||
    (selectedBuildingId ? buildingPaymentConfigQuery.isFetching : false) ||
    notifQuery.isFetching ||
    secQuery.isFetching

  const selectedBuilding = React.useMemo(
    () => buildingsQuery.data?.find((building) => building.id === selectedBuildingId) ?? null,
    [buildingsQuery.data, selectedBuildingId]
  )
  const buildingPaymentStatus = buildingPaymentConfigQuery.data?.payment_config_status ?? "MISSING"

  if (isLoading || !profileQuery.data || !notifQuery.data || !secQuery.data) {
    return (
      <div className="space-y-4">
        <Header
          onRefresh={() => {}}
          isRefreshing={false}
        />
        <Skeleton />
        <Skeleton />
        <Skeleton />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Header
        onRefresh={() => {
          profileQuery.refetch()
          paymentDetailsQuery.refetch()
          buildingsQuery.refetch()
          if (selectedBuildingId) {
            buildingPaymentConfigQuery.refetch()
          }
          notifQuery.refetch()
          secQuery.refetch()
        }}
        isRefreshing={isFetching}
      />

      {/* GRACE PERIOD WARNING */}
      {profileQuery.data?.grace_period_ends_at && new Date(profileQuery.data.grace_period_ends_at) > new Date() && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3 text-amber-800 shadow-sm relative">
          <svg className="w-6 h-6 text-amber-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
          <div className="flex-1">
            <h3 className="font-semibold text-sm">{t("settings.gracePeriodTitle")}</h3>
            <p className="text-xs opacity-90 mt-1">
              {t("settings.gracePeriodMessage", {
                date: new Date(profileQuery.data.grace_period_ends_at).toLocaleDateString(locale),
              })}
            </p>
          </div>
        </div>
      )}

      {/* Profile Information */}
      <Card title={t("profile.personalInfo")} subtitle={t("profile.personalInfoDesc")}>
        <ProfileForm
          initial={profileQuery.data}
          saving={updateProfileMut.isPending}
          onSave={(data) => updateProfileMut.mutate({ id: profileQuery.data.id, data })}
        />
      </Card>

      {/* Language */}
      <Card title={t("profile.language")} subtitle={t("profile.languageDesc")}>
        <LanguageSelector />
      </Card>

      {/* Unit Slots - Now Functional */}
      <Card title={t("settings.unitSlotsTitle")} subtitle={t("settings.unitSlotsSubtitle")}>
        <UnitSlotsCard
          planUnits={profileQuery.data?.max_units_allowed ?? 50}
          usedUnits={profileQuery.data?.current_unit_count ?? 0}
          canAddUnits={profileQuery.data?.can_add_units ?? true}
          subscriptionStatus={profileQuery.data?.subscription_status ?? "active"}
        />
      </Card>

      {/* Payment Details - NEW SECTION */}
      <Card title={t("settings.paymentDetails")} subtitle={t("settings.paymentDetailsSubtitle")}>
        <PaymentDetailsForm
          initial={paymentDetailsQuery.data ?? null}
          saving={createPaymentMut.isPending || updatePaymentMut.isPending}
          onSave={(data) => {
            if (paymentDetailsQuery.data?.id) {
              updatePaymentMut.mutate({ id: paymentDetailsQuery.data.id, data })
            } else {
              createPaymentMut.mutate(data)
            }
          }}
        />
      </Card>

      {/* Building-level Payment Details */}
      <Card title="Building Payment Details" subtitle="Configure payment destination per building.">
        {buildingsQuery.data && buildingsQuery.data.length > 0 ? (
          <div className="space-y-4">
            <InputRow label="Building">
              <select
                className="w-full rounded-xl border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[--brand]/30"
                style={{ ["--brand" as any]: PRIMARY }}
                value={selectedBuildingId}
                onChange={(e) => setSelectedBuildingId(e.target.value)}
              >
                {buildingsQuery.data.map((building: BuildingList) => (
                  <option key={building.id} value={building.id}>
                    {building.name} ({building.code})
                  </option>
                ))}
              </select>
            </InputRow>

            <div className="rounded-lg border border-black/10 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              <div>
                Status: <span className="font-medium">{buildingPaymentStatus}</span>
              </div>
              {selectedBuilding && (
                <div className="text-xs text-gray-500 mt-1">
                  Building: {selectedBuilding.name} ({selectedBuilding.code})
                </div>
              )}
              {buildingPaymentConfigQuery.data?.fallback_payment_details && buildingPaymentStatus !== "CONFIGURED" && (
                <div className="text-xs text-gray-600 mt-1">
                  Using HOA default payment details as fallback until this building is configured.
                </div>
              )}
            </div>

            <PaymentDetailsForm
              initial={(buildingPaymentConfigQuery.data?.config as any) ?? null}
              saving={updateBuildingPaymentMut.isPending}
              onSave={(data) => {
                if (!selectedBuildingId) {
                  toast.error("Select a building first")
                  return
                }
                updateBuildingPaymentMut.mutate({ buildingId: selectedBuildingId, data })
              }}
            />
          </div>
        ) : (
          <p className="text-sm text-gray-600">No buildings found for this HOA admin.</p>
        )}
      </Card>

      {/* Subscription Management */}
      <Card title={t("settings.mySubscriptionTitle")} subtitle={t("settings.mySubscriptionSubtitle")}>
        <SubscriptionSection />
      </Card>

      {/* Notification Settings */}
      <div className="relative">
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-4 rounded-2xl backdrop-blur-sm bg-white/60">
          <div className="flex flex-col items-center gap-3 rounded-2xl bg-white/90 ring-1 ring-black/10 shadow-xl px-8 py-7 text-center max-w-xs mx-4">
            <span className="text-4xl">🚧</span>
            <h2 className="text-xl font-semibold text-gray-900">{t("common.comingSoon")}</h2>
            <p className="text-sm text-gray-500 leading-relaxed">{t("settings.notificationsComingSoonDesc")}</p>
            <span className="inline-block rounded-full bg-[#1F3A93]/10 text-[#1F3A93] text-xs font-medium px-3 py-1">{t("common.inDevelopment")}</span>
          </div>
        </div>
        <div className="blur-[2px] pointer-events-none select-none">
      <Card title={t("settings.notificationsTitle")} subtitle={t("settings.notificationsSubtitle")}>
        <NotificationForm
          initial={notifQuery.data}
          saving={updateNotifMut.isPending}
          onSave={(data) => updateNotifMut.mutate(data)}
        />
      </Card>
        </div>
      </div>

      {/* Security & Access */}
      <div className="relative">
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-4 rounded-2xl backdrop-blur-sm bg-white/60">
          <div className="flex flex-col items-center gap-3 rounded-2xl bg-white/90 ring-1 ring-black/10 shadow-xl px-8 py-7 text-center max-w-xs mx-4">
            <span className="text-4xl">🚧</span>
            <h2 className="text-xl font-semibold text-gray-900">{t("common.comingSoon")}</h2>
            <p className="text-sm text-gray-500 leading-relaxed">{t("settings.securityComingSoonDesc")}</p>
            <span className="inline-block rounded-full bg-[#1F3A93]/10 text-[#1F3A93] text-xs font-medium px-3 py-1">{t("common.inDevelopment")}</span>
          </div>
        </div>
        <div className="blur-[2px] pointer-events-none select-none">
      <Card title={t("settings.securityAccessTitle")} subtitle={t("settings.securityAccessSubtitle")}>
        <SecurityForm
          initial={secQuery.data}
          saving={updateSecMut.isPending}
          onSave={(data) => updateSecMut.mutate(data)}
        />
      </Card>
        </div>
      </div>

      {/* App (PWA) */}
      <Card title={t("settings.applicationTitle")} subtitle={t("settings.applicationSubtitle")}>
        <InstallAppSection />
      </Card>

      {/* App Version & Updates */}
      <Card title={t("settings.appVersionTitle")} subtitle={t("settings.appVersionSubtitle")}>
        <AppVersionSection />
      </Card>

      {/* Account security */}
      <Card title={t("settings.accountSecurityTitle")} subtitle={t("settings.accountSecuritySubtitle")}>
        <ChangePassword />
      </Card>

      {/* Maintenance */}
      <Card title={t("settings.maintenance")} subtitle={t("settings.maintenanceSubtitle")}>
        <MaintenanceSection />
      </Card>
    </div>
  )
}

/* =============== Sections & Forms =============== */
function Header({ onRefresh, isRefreshing }: { onRefresh: () => void; isRefreshing: boolean }) {
  const { t } = useLanguage()

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 className="text-[18px] font-semibold text-gray-900">{t("settings.configurations")}</h1>
        <p className="text-sm text-gray-600">{t("settings.manageProfileMsg")}</p>
      </div>
      <button className={`inline-flex items-center justify-center h-9 px-3 ${QUIET}`} onClick={onRefresh}>
        {isRefreshing ? t("common.refreshing") : t("common.refresh")}
      </button>
    </div>
  )
}

function Card({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-3xl bg-white/90 backdrop-blur-xl ring-1 ring-black/10 shadow-lg p-5 sm:p-6">
      <div className="mb-3">
        <h2 className="text-[16px] font-semibold text-gray-900">{title}</h2>
        {subtitle && <p className="text-sm text-gray-600">{subtitle}</p>}
      </div>
      {children}
    </section>
  )
}

/* ---------- Language Selector (matches resident UI) ---------- */
function LanguageSelector() {

  const { language, setLanguage, t } = useLanguage()

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => setLanguage("en")}
          className={[
            "flex items-center justify-center gap-3 p-4 rounded-xl ring-1 transition-all",
            language === "en"
              ? "ring-2 ring-[#1F3A93] bg-[#1F3A93]/5"
              : "ring-black/10 bg-white hover:ring-black/20"
          ].join(" ")}
        >
          <span className="text-2xl">🇺🇸</span>
          <div className="text-left">
            <div className={[
              "font-medium",
              language === "en" ? "text-[#1F3A93]" : "text-gray-900"
            ].join(" ")}>{t("profile.english")}</div>
            <div className="text-xs text-gray-500">{t("profile.default")}</div>
          </div>
          {language === "en" && (
            <div className="ml-auto w-5 h-5 rounded-full bg-[#1F3A93] flex items-center justify-center">
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
        </button>

        <button
          type="button"
          onClick={() => setLanguage("es")}
          className={[
            "flex items-center justify-center gap-3 p-4 rounded-xl ring-1 transition-all",
            language === "es"
              ? "ring-2 ring-[#1F3A93] bg-[#1F3A93]/5"
              : "ring-black/10 bg-white hover:ring-black/20"
          ].join(" ")}
        >
          <span className="text-2xl">🇪🇸</span>
          <div className="text-left">
            <div className={[
              "font-medium",
              language === "es" ? "text-[#1F3A93]" : "text-gray-900"
            ].join(" ")}>{t("profile.spanish")}</div>
            <div className="text-xs text-gray-500">{t("profile.spanish")}</div>
          </div>
          {language === "es" && (
            <div className="ml-auto w-5 h-5 rounded-full bg-[#1F3A93] flex items-center justify-center">
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
        </button>
      </div>

      <p className="text-xs text-gray-500 text-center">
        {t("profile.languageAppliedNote")}
      </p>
    </div>
  )
}

/* ---------- Profile Form ---------- */
function ProfileForm({
  initial,
  saving,
  onSave,
}: {
  initial: HOAAdminProfile
  saving: boolean
  onSave: (data: { first_name?: string; last_name?: string; organization_name?: string }) => void
}) {
  const { t } = useLanguage();
  const [firstName, setFirstName] = React.useState(initial.first_name)
  const [lastName, setLastName] = React.useState(initial.last_name)
  const [orgName, setOrgName] = React.useState(initial.organization_name || "")

  function submit(e: React.FormEvent) {
    e.preventDefault()
    onSave({
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      organization_name: orgName.trim() || undefined,
    })
  }

  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <InputRow label={t("settings.firstName")}>
          <input
            className="w-full rounded-xl border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[--brand]/30"
            style={{ ["--brand" as any]: PRIMARY }}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
          />
        </InputRow>

        <InputRow label={t("settings.lastName")}>
          <input
            className="w-full rounded-xl border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[--brand]/30"
            style={{ ["--brand" as any]: PRIMARY }}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
          />
        </InputRow>
      </div>

      <InputRow label={t("settings.readOnlyEmail")}>
        <input
          className="w-full rounded-xl border border-black/10 bg-gray-100 px-3 py-2 text-sm outline-none"
          value={initial.email}
          disabled
        />
      </InputRow>

      <InputRow label={t("settings.orgName")}>
        <input
          className="w-full rounded-xl border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[--brand]/30"
          style={{ ["--brand" as any]: PRIMARY }}
          value={orgName}
          onChange={(e) => setOrgName(e.target.value)}
          placeholder={t("settings.exCondoName")}
        />
      </InputRow>

      <div className="mt-1 flex items-center justify-end">
        <button
          type="submit"
          disabled={saving}
          className="rounded-xl px-4 py-2 text-sm font-medium text-white shadow-lg hover:shadow-xl transition"
          style={{ background: PRIMARY }}
        >
          {saving ? t("settings.saving") : t("settings.saveProfile")}
        </button>
      </div>
    </form>
  )
}

/* ---------- Unit Slots (now functional) ---------- */
function UnitSlotsCard({
  planUnits,
  usedUnits,
  canAddUnits,
  subscriptionStatus,
}: {
  planUnits: number
  usedUnits: number
  canAddUnits: boolean
  subscriptionStatus: string
}) {
  const { t } = useLanguage()

  const remaining = Math.max(0, planUnits - usedUnits)
  const pct = planUnits ? Math.min(100, Math.round((usedUnits / planUnits) * 100)) : 0
  const low = remaining <= 5
  const statusKeyMap: Record<string, string> = {
    trial: "settings.subscriptionStatus.trial",
    active: "settings.subscriptionStatus.active",
    suspended: "settings.subscriptionStatus.suspended",
    cancelled: "settings.subscriptionStatus.cancelled",
    expired: "settings.subscriptionStatus.expired",
  }
  const statusColor =
    subscriptionStatus === "active" ? "text-green-700" : subscriptionStatus === "suspended" ? "text-amber-700" : "text-red-700"

  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[15px] font-semibold text-gray-900">{t("settings.planUsage")}</div>
          <div className="mt-1 text-sm text-gray-600">
            {t("settings.unitsUsed", { used: usedUnits, total: planUnits })}
          </div>
          <div className={`mt-1 text-xs font-medium ${statusColor}`}>
            {t("common.status")}: {t(statusKeyMap[subscriptionStatus] ?? "common.unknown")}
          </div>
        </div>
      </div>

      <div className="mt-3">
        <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: low ? "#D97706" : "#0a0a0a" }} />
        </div>
        <div className="mt-2 flex items-center justify-between text-xs">
          <span className="text-gray-600">{t("settings.percentUsed", { percent: pct })}</span>
          <span
            className="inline-flex items-center rounded-full px-2 py-1 font-medium"
            style={{
              background: low ? "#FEF3C7" : "#F5F6FA",
              color: low ? "#B45309" : "#111827",
            }}
          >
            {t("settings.remainingUnits", { count: remaining })} {low && `• ${t("settings.low")}`}
          </span>
        </div>
      </div>

      {!canAddUnits && (
        <div className="mt-3 rounded-lg bg-rose-50 border border-rose-200 p-3">
          <p className="text-sm text-rose-800">
            ⚠️ {t("settings.unitsLimitReached")}
          </p>
        </div>
      )}
    </div>
  )
}

/* ---------- Payment Details Form - NEW ---------- */
function PaymentDetailsForm({
  initial,
  saving,
  onSave,
}: {
  initial: PaymentDetails | null
  saving: boolean
  onSave: (data: UpdatePaymentDetailsData) => void
}) {
  const { t } = useLanguage();
  const [form, setForm] = React.useState<UpdatePaymentDetailsData>({
    bank_name: initial?.bank_name || "",
    bank_account_number: initial?.bank_account_number || "",
    bank_routing_number: initial?.bank_routing_number || "",
    account_holder_name: initial?.account_holder_name || "",
    zelle_email: initial?.zelle_email || "",
    zelle_phone: initial?.zelle_phone || "",
    paypal_email: initial?.paypal_email || "",
    venmo_username: initial?.venmo_username || "",
    cashapp_username: initial?.cashapp_username || "",
    pago_movil_bank: initial?.pago_movil_bank || "",
    pago_movil_phone: initial?.pago_movil_phone || "",
    pago_movil_id_number: initial?.pago_movil_id_number || "",
    cash_instructions: initial?.cash_instructions || "",
  })

  function submit(e: React.FormEvent) {
    e.preventDefault()
    // Only send non-empty fields
    const dataToSend: UpdatePaymentDetailsData = {}
    Object.entries(form).forEach(([key, value]) => {
      if (value && value.trim()) {
        dataToSend[key as keyof UpdatePaymentDetailsData] = value.trim()
      }
    })
    onSave(dataToSend)
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      {initial?.is_verified && (
        <div className="rounded-lg bg-green-50 border border-green-200 p-3">
          <p className="text-sm text-green-800">✓ {t("settings.paymentDetailsVerified")}</p>
        </div>
      )}

      {/* Bank Transfer */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-900">{t("settings.bankTransferDetails")}</h3>

        <InputRow label={t("settings.bankName")}>
          <input
            className="w-full rounded-xl border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[--brand]/30"
            style={{ ["--brand" as any]: PRIMARY }}
            value={form.bank_name}
            onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
            placeholder={t("settings.exBanesco")}
          />
        </InputRow>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <InputRow label={t("settings.accountNumber")}>
            <input
              className="w-full rounded-xl border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[--brand]/30"
              style={{ ["--brand" as any]: PRIMARY }}
              value={form.bank_account_number}
              onChange={(e) => setForm({ ...form, bank_account_number: e.target.value })}
              placeholder={t("settings.accountNumberPlaceholder")}
            />
          </InputRow>

          <InputRow label={t("settings.routingNumber")}>
            <input
              className="w-full rounded-xl border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[--brand]/30"
              style={{ ["--brand" as any]: PRIMARY }}
              value={form.bank_routing_number}
              onChange={(e) => setForm({ ...form, bank_routing_number: e.target.value })}
              placeholder={t("settings.routingPlaceholder")}
            />
          </InputRow>
        </div>

        <InputRow label={t("settings.accountHolderName")}>
          <input
            className="w-full rounded-xl border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[--brand]/30"
            style={{ ["--brand" as any]: PRIMARY }}
            value={form.account_holder_name}
            onChange={(e) => setForm({ ...form, account_holder_name: e.target.value })}
            placeholder={t("settings.accountHolderPlaceholder")}
          />
        </InputRow>
      </div>

      {/* Zelle */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-900">{t("settings.paymentMethod.zelle")}</h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <InputRow label={t("settings.zelleEmail")}>
            <input
              type="email"
              className="w-full rounded-xl border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[--brand]/30"
              style={{ ["--brand" as any]: PRIMARY }}
              value={form.zelle_email}
              onChange={(e) => setForm({ ...form, zelle_email: e.target.value })}
              placeholder={t("settings.zelleEmailPlaceholder")}
            />
          </InputRow>

          <InputRow label={t("settings.zellePhone")}>
            <PhoneInputField
              className="bg-white"
              value={form.zelle_phone ?? ""}
              onChange={(val) => setForm({ ...form, zelle_phone: val || "" })}
              placeholder="+1 (555) 123-4567"
            />
          </InputRow>
        </div>
      </div>

      {/* Pago Movil */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-900">{t("settings.paymentMethod.pagoMovil")}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <InputRow label={t("settings.bank")}>
            <input
              className="w-full rounded-xl border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[--brand]/30"
              style={{ ["--brand" as any]: PRIMARY }}
              value={form.pago_movil_bank}
              onChange={(e) => setForm({ ...form, pago_movil_bank: e.target.value })}
              placeholder={t("settings.exMercantil")}
            />
          </InputRow>
          <InputRow label={t("settings.idNumber")}>
            <input
              className="w-full rounded-xl border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[--brand]/30"
              style={{ ["--brand" as any]: PRIMARY }}
              value={form.pago_movil_id_number}
              onChange={(e) => setForm({ ...form, pago_movil_id_number: e.target.value })}
              placeholder={t("settings.exIdNumber")}
            />
          </InputRow>
          <InputRow label={t("settings.phone")}>
            <PhoneInputField
              className="bg-white"
              value={form.pago_movil_phone ?? ""}
              onChange={(val) => setForm({ ...form, pago_movil_phone: val || "" })}
              placeholder="+58 (414) 123-4567"
            />
          </InputRow>
        </div>
      </div>

      {/* Cash Instructions */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-900">{t("settings.cashInstructionsTitle")}</h3>
        <InputRow label={t("settings.instructions")}>
          <textarea
            className="w-full min-h-[80px] rounded-xl border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[--brand]/30 resize-y"
            style={{ ["--brand" as any]: PRIMARY }}
            value={form.cash_instructions}
            onChange={(e) => setForm({ ...form, cash_instructions: e.target.value })}
            placeholder={t("settings.cashInstructionsPlaceholder")}
          />
        </InputRow>
      </div>

      {/* Otros Métodos de Pago */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-900">{t("settings.otherPaymentMethods")}</h3>

        <InputRow label={t("settings.paypalEmail")}>
          <input
            type="email"
            className="w-full rounded-xl border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[--brand]/30"
            style={{ ["--brand" as any]: PRIMARY }}
            value={form.paypal_email}
            onChange={(e) => setForm({ ...form, paypal_email: e.target.value })}
            placeholder={t("settings.paypalEmailPlaceholder")}
          />
        </InputRow>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <InputRow label={t("settings.venmoUsername")}>
            <input
              className="w-full rounded-xl border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[--brand]/30"
              style={{ ["--brand" as any]: PRIMARY }}
              value={form.venmo_username}
              onChange={(e) => setForm({ ...form, venmo_username: e.target.value })}
              placeholder={t("settings.venmoPlaceholder")}
            />
          </InputRow>

          <InputRow label={t("settings.cashAppUsername")}>
            <input
              className="w-full rounded-xl border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[--brand]/30"
              style={{ ["--brand" as any]: PRIMARY }}
              value={form.cashapp_username}
              onChange={(e) => setForm({ ...form, cashapp_username: e.target.value })}
              placeholder={t("settings.cashAppPlaceholder")}
            />
          </InputRow>
        </div>
      </div>

      <div className="pt-2 flex items-center justify-end">
        <button
          type="submit"
          disabled={saving}
          className="rounded-xl px-4 py-2 text-sm font-medium text-white shadow-lg hover:shadow-xl transition"
          style={{ background: PRIMARY }}
        >
          {saving ? t("settings.saving") : initial ? t("settings.updatePaymentDetails") : t("settings.createPaymentDetails")}
        </button>
      </div>
    </form>
  )
}

/* ---------- Subscription Management ---------- */
const CYCLE_LABEL_KEY: Record<string, string> = {
  monthly: "settings.billingCycleMonthly",
  quarterly: "settings.billingCycleQuarterly",
  annual: "settings.billingCycleAnnual",
}

const SUB_STATUS_COLORS: Record<string, { bg: string; fg: string; labelKey: string }> = {
  trial:      { bg: "bg-blue-50",   fg: "text-blue-700",  labelKey: "settings.subscriptionStatus.trial" },
  active:     { bg: "bg-green-50",  fg: "text-green-700", labelKey: "settings.subscriptionStatus.active" },
  suspended:  { bg: "bg-amber-50",  fg: "text-amber-700", labelKey: "settings.subscriptionStatus.suspended" },
  cancelled:  { bg: "bg-gray-100",  fg: "text-gray-600",  labelKey: "settings.subscriptionStatus.cancelled" },
  expired:    { bg: "bg-rose-50",   fg: "text-rose-700",  labelKey: "settings.subscriptionStatus.expired" },
}

const INV_STATUS_COLORS: Record<string, { bg: string; fg: string; labelKey: string }> = {
  PENDING_PAYMENT:      { bg: "bg-amber-50",  fg: "text-amber-700", labelKey: "settings.invoiceStatus.pendingPayment" },
  PENDING_VERIFICATION: { bg: "bg-blue-50",   fg: "text-blue-700",  labelKey: "settings.invoiceStatus.pendingVerification" },
  COMPLETED:            { bg: "bg-green-50",  fg: "text-green-700", labelKey: "settings.invoiceStatus.completed" },
  REJECTED:             { bg: "bg-rose-50",   fg: "text-rose-700",  labelKey: "settings.invoiceStatus.rejected" },
  CANCELLED:            { bg: "bg-gray-100",  fg: "text-gray-600",  labelKey: "settings.invoiceStatus.cancelled" },
}

/* ── Payment destination info box shown to HOA admin ── */
function PaymentDestinationBox({ details, amount }: { details: PlatformPaymentInfo; amount: string }) {
  const { t } = useLanguage()


  const hasBank = !!(details.bank_account_number || details.bank_name)
  const hasZelle = !!(details.zelle_phone || details.zelle_email)
  const hasPaypal = !!details.paypal_email
  const hasAny = hasBank || hasZelle || hasPaypal

  return (
    <div className="rounded-xl bg-amber-50 ring-1 ring-amber-200/60 p-3.5 space-y-2.5">
      <p className="text-xs font-semibold text-amber-900">
        {t("settings.sendAmountToFollowing", { amount })}
      </p>

      {details.payment_instructions && (
        <p className="text-xs text-amber-800 leading-relaxed">{details.payment_instructions}</p>
      )}

      {!hasAny && !details.payment_instructions && (
        <p className="text-xs text-amber-700">
          {t("settings.contactPlatformAdminForPayment")}
        </p>
      )}

      {hasBank && (
        <div className="rounded-lg bg-white ring-1 ring-black/5 px-3 py-2.5 space-y-1">
          <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">{t("settings.paymentMethod.bankTransfer")}</p>
          {details.bank_name && <InfoRow label={t("settings.bank")} value={details.bank_name} />}
          {details.bank_account_name && <InfoRow label={t("settings.accountHolderName")} value={details.bank_account_name} />}
          {details.bank_account_number && <InfoRow label={t("settings.accountNumber")} value={details.bank_account_number} copyable />}
          {details.bank_routing_number && <InfoRow label={t("settings.routingNumber")} value={details.bank_routing_number} copyable />}
          {details.bank_notes && <p className="text-xs text-gray-500 pt-1">{details.bank_notes}</p>}
        </div>
      )}

      {hasZelle && (
        <div className="rounded-lg bg-white ring-1 ring-black/5 px-3 py-2.5 space-y-1">
          <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">{t("settings.paymentMethod.zelle")}</p>
          {details.zelle_name && <InfoRow label={t("settings.firstName")} value={details.zelle_name} />}
          {details.zelle_phone && <InfoRow label={t("settings.phone")} value={details.zelle_phone} copyable />}
          {details.zelle_email && <InfoRow label={t("settings.email")} value={details.zelle_email} copyable />}
        </div>
      )}

      {hasPaypal && (
        <div className="rounded-lg bg-white ring-1 ring-black/5 px-3 py-2.5 space-y-1">
          <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">{t("settings.paymentMethod.paypal")}</p>
          <InfoRow label={t("settings.email")} value={details.paypal_email} copyable />
        </div>
      )}
    </div>
  )
}

function InfoRow({ label, value, copyable = false }: { label: string; value: string; copyable?: boolean }) {
  const { t } = useLanguage()


  const [copied, setCopied] = React.useState(false)
  function copy() {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-xs text-gray-500 shrink-0">{label}:</span>
      <span className="text-xs font-medium text-gray-900 text-right break-all">{value}</span>
      {copyable && (
        <button
          type="button"
          onClick={copy}
          className="shrink-0 text-[10px] rounded px-1.5 py-0.5 bg-gray-100 text-gray-600 hover:bg-gray-200 transition"
        >
          {copied ? "✓" : t("settings.copy")}
        </button>
      )}
    </div>
  )
}

function SubscriptionSection() {
  const { t, language } = useLanguage()
  const locale = language === "es" ? "es-VE" : "en-US"
  const cycleLabel = (cycle?: string) => {
    const key = cycle ? CYCLE_LABEL_KEY[cycle] : undefined
    return key ? t(key) : ""
  }

  


  const qc = useQueryClient()
  const [selectedPlan, setSelectedPlan] = React.useState<PublicSubscriptionPlan | null>(null)
  const [proofFile, setProofFile] = React.useState<File | null>(null)
  const [payMethod, setPayMethod] = React.useState<SubscriptionPaymentMethod>("BANK_TRANSFER")
  const [showHistory, setShowHistory] = React.useState(false)

  const plansQuery = useQuery({
    queryKey: ["subscription", "available-plans"],
    queryFn: listAvailablePlans,
    staleTime: 5 * 60 * 1000,
  })

  const subscriptionQuery = useQuery({
    queryKey: ["subscription", "mine"],
    queryFn: getMySubscription,
    retry: false,
  })

  const invoicesQuery = useQuery({
    queryKey: ["subscription", "my-invoices"],
    queryFn: listMyInvoices,
  })

  const requestMut = useMutation({
    mutationFn: (planId: string) => requestSubscriptionPlan(planId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["subscription"] })
      setSelectedPlan(null)
      toast.success(t("settings.subRequestCreated"))
    },
    onError: () => toast.error(t("settings.subRequestError")),
  })

  const uploadMut = useMutation({
    mutationFn: ({ id, file, method }: { id: string; file: File; method: SubscriptionPaymentMethod }) =>
      uploadSubscriptionProof(id, file, method),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["subscription"] })
      setProofFile(null)
      toast.success(t("settings.proofUploaded"))
    },
    onError: () => toast.error(t("settings.proofUploadError")),
  })

  const cancelMut = useMutation({
    mutationFn: (id: string) => cancelSubscriptionInvoice(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["subscription"] })
      toast.success(t("settings.subCancelled"))
    },
    onError: () => toast.error(t("settings.cancelError")),
  })

  const paymentDetailsQuery = useQuery({
    queryKey: ["platform", "payment-details"],
    queryFn: getPlatformPaymentInfo,
    staleTime: 10 * 60 * 1000,
  })

  const plans = plansQuery.data ?? []
  const subscription = subscriptionQuery.data ?? null
  const invoices = invoicesQuery.data ?? []
  const activeInvoice = invoices.find(
    (i) => i.status === "PENDING_PAYMENT" || i.status === "PENDING_VERIFICATION" || i.status === "REJECTED"
  ) ?? null
  const completedInvoices = invoices.filter((i) => i.status === "COMPLETED" || i.status === "CANCELLED")
  const paymentDetails = paymentDetailsQuery.data ?? null
  const subStatus = subscription?.status ?? "trial"
  const subColors = SUB_STATUS_COLORS[subStatus] ?? SUB_STATUS_COLORS.trial

  /* ── Current status banner ── */
  return (
    <div className="space-y-5">
      {/* Status row */}
      <div className="flex flex-wrap items-start gap-4 rounded-2xl bg-gray-50 ring-1 ring-black/5 px-4 py-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${subColors.bg} ${subColors.fg}`}>
              {t(subColors.labelKey)}
            </span>
            <span className="text-sm font-semibold text-gray-900">
              {subscription?.plan_detail?.name || t("settings.noActivePlan")}
            </span>
          </div>
          {subscription?.plan_detail && (
            <p className="mt-1 text-sm text-gray-600">
              {t("settings.upTo")} {subscription.plan_detail.max_units} {t("settings.unitsLowercase")}
              {" · "}${subscription.plan_detail.price_usd} {cycleLabel(subscription.plan_detail.billing_cycle)}
            </p>
          )}
          {subscription?.trial_ends_at && subStatus === "trial" && (
            <p className="mt-1 text-xs text-amber-600">
              {t("settings.trialEnds")} {new Date(subscription.trial_ends_at).toLocaleDateString(locale)}
            </p>
          )}
          {subscription?.current_period_end && subStatus === "active" && (
            <p className="mt-1 text-xs text-gray-500">
              {t("settings.renewsOn")} {new Date(subscription.current_period_end).toLocaleDateString(locale)}
            </p>
          )}
        </div>
        {subscriptionQuery.isLoading && (
          <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
        )}
      </div>

      {/* Active invoice gate */}
      {activeInvoice ? (
        <div className="space-y-3">
          {/* Invoice status card */}
          <div className={`rounded-2xl px-4 py-4 ring-1 ring-black/5 ${INV_STATUS_COLORS[activeInvoice.status]?.bg ?? "bg-gray-50"}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${INV_STATUS_COLORS[activeInvoice.status]?.bg} ${INV_STATUS_COLORS[activeInvoice.status]?.fg}`}>
                    {t(INV_STATUS_COLORS[activeInvoice.status]?.labelKey ?? "settings.invoiceStatus.pendingPayment")}
                  </span>
                  <span className="text-sm font-semibold text-gray-900">
                    {activeInvoice.plan_detail?.name ?? t("settings.plan")}
                  </span>
                </div>
                <p className="mt-1 text-sm text-gray-700">
                  ${activeInvoice.amount_usd} {cycleLabel(activeInvoice.billing_cycle)}
                </p>
                {activeInvoice.rejection_reason && (
                  <p className="mt-1 text-xs text-rose-700 font-medium">
                    {t("settings.rejectedReason")}: {activeInvoice.rejection_reason}
                  </p>
                )}
              </div>
              {activeInvoice.status === "PENDING_PAYMENT" && (
                <button
                  onClick={() => cancelMut.mutate(activeInvoice.id)}
                  disabled={cancelMut.isPending}
                  className="text-xs text-gray-500 hover:text-rose-600 underline"
                >{t("settings.cancelRequest")}</button>
              )}
            </div>
          </div>

          {/* Upload proof form */}
          {(activeInvoice.status === "PENDING_PAYMENT" || activeInvoice.status === "REJECTED") && (
            <div className="rounded-2xl bg-white ring-1 ring-black/8 p-4 space-y-3">
              <p className="text-sm font-semibold text-gray-900">{t("settings.uploadProof")}</p>

              {/* Payment destination details */}
              {paymentDetails ? (
                <PaymentDestinationBox details={paymentDetails} amount={activeInvoice.amount_usd} />
              ) : (
                <p className="text-xs text-gray-500">
                  {t("settings.transferPrefix")} ${activeInvoice.amount_usd} {t("settings.transferSuffix")}
                </p>
              )}

              {/* Payment method */}
              <div className="flex flex-wrap gap-2">
                {(["BANK_TRANSFER", "ZELLE", "STRIPE", "PAGO_MOVIL", "CASH"] as SubscriptionPaymentMethod[]).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setPayMethod(m)}
                    className={`rounded-xl border px-3 py-2 text-xs font-medium transition flex-1 ${payMethod === m ? "border-[#1F3A93] bg-[#1F3A93]/5 text-[#1F3A93]" : "border-black/10 text-gray-700 hover:bg-gray-50"}`}
                  >
                      {m === "BANK_TRANSFER"
                        ? t("settings.paymentMethod.bankTransfer")
                        : m === "ZELLE"
                          ? t("settings.paymentMethod.zelle")
                          : m === "STRIPE"
                            ? t("settings.paymentMethod.cardStripe")
                            : m === "PAGO_MOVIL"
                              ? t("settings.paymentMethod.pagoMovil")
                              : t("settings.paymentMethod.cash")}
                  </button>
                ))}
              </div>

              {/* File picker */}
              <label className="block">
                <span className="text-xs font-medium text-gray-700">{t("settings.screenshotReceipt")}</span>
                <div className={`mt-1 flex items-center justify-center rounded-xl border-2 border-dashed px-4 py-5 text-center cursor-pointer transition ${proofFile ? "border-[#1F3A93] bg-[#1F3A93]/5" : "border-gray-200 bg-gray-50 hover:bg-gray-100"}`}>
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    className="sr-only"
                    onChange={(e) => setProofFile(e.target.files?.[0] ?? null)}
                  />
                  {proofFile ? (
                    <span className="text-sm text-[#1F3A93] font-medium">{proofFile.name}</span>
                  ) : (
                    <span className="text-sm text-gray-500">{t("settings.clickToSelectFile")}</span>
                  )}
                </div>
              </label>

              <button
                type="button"
                disabled={!proofFile || uploadMut.isPending}
                onClick={() => proofFile && uploadMut.mutate({ id: activeInvoice.id, file: proofFile, method: payMethod })}
                className="w-full rounded-xl py-2.5 text-sm font-semibold text-white shadow disabled:opacity-50 transition"
                style={{ background: PRIMARY }}
              >
                {uploadMut.isPending ? t("settings.uploading") : t("settings.submitProof")}
              </button>

              {activeInvoice.payment_proof_url && (
                <a href={activeInvoice.payment_proof_url} target="_blank" rel="noopener noreferrer"
                   className="block text-xs text-[#1F3A93] underline text-center">{t("settings.viewPreviousProof")}</a>
              )}
            </div>
          )}

          {/* Pending verification message */}
          {activeInvoice.status === "PENDING_VERIFICATION" && (
            <div className="rounded-2xl bg-blue-50 ring-1 ring-blue-200/50 px-4 py-4 text-center">
              <div className="text-2xl mb-1">⏳</div>
              <p className="text-sm font-semibold text-blue-900">{t("settings.waitingAdminVerification")}</p>
              <p className="text-xs text-blue-700 mt-1">
                {t("settings.proofReceivedMessage")}
              </p>
              {activeInvoice.payment_proof_url && (
                <a href={activeInvoice.payment_proof_url} target="_blank" rel="noopener noreferrer"
                   className="mt-2 inline-block text-xs text-blue-700 underline">{t("settings.viewUploadedProof")}</a>
              )}
            </div>
          )}
        </div>
      ) : (
        /* ── Plan Grid (no active invoice) ── */
        <div className="space-y-3">
          {plansQuery.isLoading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[1, 2].map((i) => (
                <div key={i} className="h-28 animate-pulse rounded-2xl bg-gray-100" />
              ))}
            </div>
          )}
          {!plansQuery.isLoading && plans.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-4">{t("settings.noPlansAvailable")}</p>
          )}
          {plans.length > 0 && (
            <>
              <p className="text-sm font-medium text-gray-700">{t("settings.availablePlans")}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {plans.map((plan) => {
                  const isCurrent = subscription?.plan_detail?.id === plan.id
                  const isSelected = selectedPlan?.id === plan.id
                  return (
                    <div
                      key={plan.id}
                      className={`rounded-2xl ring-1 px-4 py-4 flex flex-col gap-2 transition cursor-pointer ${isSelected ? "ring-[#1F3A93] bg-[#1F3A93]/5" : isCurrent ? "ring-black/10 bg-green-50/60" : "ring-black/8 bg-white hover:bg-gray-50"}`}
                      onClick={() => setSelectedPlan(isSelected ? null : plan)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-[15px] font-semibold text-gray-900">{plan.name}</p>
                          <p className="text-xs text-gray-500">{plan.description}</p>
                        </div>
                        {isCurrent && (
                          <span className="shrink-0 rounded-full bg-green-100 text-green-700 text-[10px] font-semibold px-2 py-0.5">{t("settings.current")}</span>
                        )}
                      </div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-xl font-bold text-gray-900">${plan.price_usd}</span>
                        <span className="text-xs text-gray-500">{cycleLabel(plan.billing_cycle)}</span>
                      </div>
                      <p className="text-xs text-gray-500">{t("settings.upTo")} {plan.max_units === 0 ? t("settings.unlimited") : plan.max_units} {t("settings.unitsLowercase")}</p>
                    </div>
                  )
                })}
              </div>

              {selectedPlan && (
                <div className="rounded-2xl bg-[#1F3A93]/5 ring-1 ring-[#1F3A93]/20 px-4 py-4 space-y-3">
                  <p className="text-sm font-semibold text-gray-900">
                    {t("settings.subscribeTo")} <span className="text-[#1F3A93]">{selectedPlan.name}</span>
                    {" "}- ${selectedPlan.price_usd} {cycleLabel(selectedPlan.billing_cycle)}
                  </p>
                  <p className="text-xs text-gray-600">
                    {t("settings.afterConfirmingMsg")}
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => requestMut.mutate(selectedPlan.id)}
                      disabled={requestMut.isPending}
                      className="flex-1 rounded-xl py-2 text-sm font-semibold text-white shadow disabled:opacity-50"
                      style={{ background: PRIMARY }}
                    >
                      {requestMut.isPending ? t("settings.requesting") : t("settings.confirmAndRequest")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedPlan(null)}
                      className={`rounded-xl px-4 py-2 text-sm ${QUIET}`}
                    >{t("settings.cancel")}</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Invoice history */}
      {completedInvoices.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowHistory(!showHistory)}
            className="text-xs text-gray-500 hover:text-gray-900 underline"
          >
            {showHistory ? t("settings.hide") : t("settings.show")} {t("settings.paymentHistory")} ({completedInvoices.length})
          </button>
          {showHistory && (
            <div className="mt-3 space-y-2">
              {completedInvoices.map((inv) => (
                <div key={inv.id} className="flex items-start justify-between gap-2 rounded-xl bg-gray-50 ring-1 ring-black/5 px-3 py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{inv.plan_detail?.name ?? t("settings.plan")}</p>
                    <p className="text-xs text-gray-500">
                      ${inv.amount_usd} · {new Date(inv.created_at).toLocaleDateString(locale)}
                    </p>
                  </div>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${INV_STATUS_COLORS[inv.status]?.bg} ${INV_STATUS_COLORS[inv.status]?.fg}`}>
                    {t(INV_STATUS_COLORS[inv.status]?.labelKey ?? "settings.invoiceStatus.pendingPayment")}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/* ---------- Notification Settings ---------- */
function NotificationForm({ initial, saving, onSave }: { initial: NotificationPrefs; saving: boolean; onSave: (p: NotificationPrefs) => void }) {
  const { t } = useLanguage()
  const [form, setForm] = React.useState<NotificationPrefs>(initial)
  function submit(e: React.FormEvent) { e.preventDefault(); onSave(form) }

  return (
    <form onSubmit={submit} className="space-y-2">
      <ToggleRow label={t("settings.emailNotifications")} description={t("settings.emailNotifDesc")} checked={form.email} onChange={(v) => setForm({ ...form, email: v })} />
      <ToggleRow label={t("settings.smsNotifications")} description={t("settings.smsNotifDesc")} checked={form.sms} onChange={(v) => setForm({ ...form, sms: v })} />
      <ToggleRow label={t("settings.maintenanceAlerts")} description={t("settings.maintenanceNotifDesc")} checked={form.maintenanceAlerts} onChange={(v) => setForm({ ...form, maintenanceAlerts: v })} />
      <ToggleRow label={t("settings.paymentReminders")} description={t("settings.paymentRemindersDesc")} checked={form.paymentReminders} onChange={(v) => setForm({ ...form, paymentReminders: v })} />
      <div className="pt-2 flex items-center justify-end">
        <button type="submit" disabled={saving}
                className="rounded-xl px-4 py-2 text-sm font-medium text-white shadow-lg hover:shadow-xl transition"
                style={{ background: PRIMARY }}>
          {saving ? t("settings.saving") : t("settings.save")}
        </button>
      </div>
    </form>
  )
}

/* ---------- Security & Access ---------- */
function SecurityForm({ initial, saving, onSave }: { initial: SecurityAccess; saving: boolean; onSave: (p: SecurityAccess) => void }) {
  const { t } = useLanguage()
  const [form, setForm] = React.useState<SecurityAccess>(initial)
  function submit(e: React.FormEvent) { e.preventDefault(); onSave(form) }

  return (
    <form onSubmit={submit} className="space-y-2">
      <ToggleRow label={t("settings.requirePhotos")} description={t("settings.requirePhotosDesc")} checked={form.requerirFotosProblemas} onChange={(v) => setForm({ ...form, requerirFotosProblemas: v })} />
      <ToggleRow label={t("settings.autoApproveResidents")} description={t("settings.autoApproveDesc")} checked={form.autoAprobarResidentes} onChange={(v) => setForm({ ...form, autoAprobarResidentes: v })} />
      <ToggleRow label={t("settings.allowGuests")} description={t("settings.allowGuestsDesc")} checked={form.permitirAccesoInvitados} onChange={(v) => setForm({ ...form, permitirAccesoInvitados: v })} />
      <div className="pt-2 flex items-center justify-end">
        <button type="submit" disabled={saving}
                className="rounded-xl px-4 py-2 text-sm font-medium text-white shadow-lg hover:shadow-xl transition"
                style={{ background: PRIMARY }}>
          {saving ? t("settings.saving") : t("settings.save")}
        </button>
      </div>
    </form>
  )
}

/* ---------- App (PWA) ---------- */
type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>
}
function InstallAppSection() {
  const { t } = useLanguage()

  


  const [deferredPrompt, setDeferredPrompt] = React.useState<BeforeInstallPromptEvent | null>(null)
  const [installed, setInstalled] = React.useState<boolean>(isStandalone())

  React.useEffect(() => {
    function onBIP(e: Event) {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
    }
    function onInstalled() {
      setInstalled(true)
      setDeferredPrompt(null)
    }
    window.addEventListener("beforeinstallprompt", onBIP)
    window.addEventListener("appinstalled", onInstalled)
    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP)
      window.removeEventListener("appinstalled", onInstalled)
    }
  }, [])

  async function handleInstall() {
    if (!deferredPrompt) return
    await deferredPrompt.prompt()
    await deferredPrompt.userChoice
    setDeferredPrompt(null)
  }

  const ua = navigator.userAgent
  const isIOS = /iphone|ipad|ipod/i.test(ua)
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua)

  if (installed) return <p className="text-sm text-green-700">✅ {t("settings.appInstalled")}</p>

  return (
    <div className="space-y-2">
      {isIOS && isSafari ? (
        <>
          <button type="button" className="rounded-xl px-4 py-2 text-sm font-medium text-white shadow-lg hover:shadow-xl transition" style={{ background: PRIMARY }}>
            📲 {t("settings.howToInstallIOS")}
          </button>
          <p className="text-xs text-gray-600">{t("settings.iosInstallTip")}</p>
        </>
      ) : (
        <>
          <button
            onClick={handleInstall}
            disabled={!deferredPrompt}
            className="rounded-xl px-4 py-2 text-sm font-medium text-white shadow-lg hover:shadow-xl transition disabled:opacity-60"
            style={{ background: PRIMARY }}
            title={deferredPrompt ? t("settings.installApplication") : t("settings.waitingBrowserInstall")}
          >
            📲 {t("profile.installApp")}
          </button>
          {!deferredPrompt && (
            <p className="text-xs text-gray-600">{t("settings.installTip")}</p>
          )}
        </>
      )}
    </div>
  )
}
function isStandalone() {
  return (
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    (window.navigator as any).standalone === true
  )
}

/* ---------- Account Security ---------- */
function ChangePassword() {
  const { t } = useLanguage()

  


  const [current, setCurrent] = React.useState("")
  const [next, setNext] = React.useState("")
  const [confirm, setConfirm] = React.useState("")
  const [show, setShow] = React.useState(false)

  const pwMut = useMutation({
    mutationFn: () => changePassword(current, next),
    onSuccess: () => {
      setCurrent(""); setNext(""); setConfirm("")
      toast.success(t("settings.passwordUpdated"))
    },
    onError: (err: any) => {
      const detail = err?.response?.data?.error ||
        err?.response?.data?.current_password?.[0] ||
        err?.response?.data?.new_password?.[0] ||
        t("settings.passwordUpdateError")
      toast.error(detail)
    },
  })

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (next.length < 8) { toast.error(t("settings.passwordMinLength")); return }
    if (next !== confirm) { toast.error(t("settings.passwordsDoNotMatch")); return }
    pwMut.mutate()
  }

  const type = show ? "text" : "password"

  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-3">
      <Labeled label={t("settings.currentPassword")}><input type={type} value={current} onChange={(e) => setCurrent(e.target.value)} className="w-full rounded-xl border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[--brand]/30" style={{ ["--brand" as any]: PRIMARY }} required /></Labeled>
      <Labeled label={t("settings.newPassword")}><input type={type} value={next} onChange={(e) => setNext(e.target.value)} className="w-full rounded-xl border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[--brand]/30" style={{ ["--brand" as any]: PRIMARY }} required /></Labeled>
      <Labeled label={t("settings.confirmPassword")}><input type={type} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="w-full rounded-xl border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[--brand]/30" style={{ ["--brand" as any]: PRIMARY }} required /></Labeled>

      <div className="flex items-center justify-between">
        <label className="inline-flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} />
          {t("settings.showPasswords")}
        </label>
        <button type="submit" disabled={pwMut.isPending}
                className="rounded-xl px-4 py-2 text-sm font-medium text-white shadow-lg hover:shadow-xl transition disabled:opacity-60"
                style={{ background: PRIMARY }}>
          {pwMut.isPending ? t("settings.saving") : t("settings.updatePassword")}
        </button>
      </div>
    </form>
  )
}

/* ---------- Maintenance ---------- */
function MaintenanceSection() {
  const { t, language } = useLanguage()
  const locale = language === "es" ? "es-VE" : "en-US"


  const [isRepairing, setIsRepairing] = React.useState(false)
  const [repairType, setRepairType] = React.useState<'quick' | 'full' | null>(null)

  async function handleQuickRepair() {
    if (!confirm(t("settings.quickRepairConfirm"))) return
    
    setIsRepairing(true)
    setRepairType('quick')
    
    try {
      const { quickRepair } = await import('@/utils/app-repair')
      await quickRepair()
    } catch (error) {
      console.error('Quick repair failed:', error)
      alert(t("settings.quickRepairFailed"))
      setIsRepairing(false)
      setRepairType(null)
    }
  }

  async function handleFullRepair() {
    if (!confirm(t("settings.fullRepairConfirm"))) return
    
    setIsRepairing(true)
    setRepairType('full')
    
    try {
      const { fullRepair } = await import('@/utils/app-repair')
      const result = await fullRepair()
      
      if (!result.success) {
        const failed = result.steps.filter(s => !s.success)
        console.error('Algunos pasos de la reparación fallaron:', failed)
        alert(t("settings.repairCompletedWithErrors", { count: failed.length }))
      }
    } catch (error) {
      console.error('La reparación completa falló:', error)
      alert(t("settings.fullRepairFailed"))
      setIsRepairing(false)
      setRepairType(null)
    }
  }

  async function showAppInfo() {
    try {
      const { getServiceWorkerState } = await import('@/utils/sw-registration')
      const state = await getServiceWorkerState()
      
      const info = [
        `${t("settings.appVersionLabel")}: ${state.appVersion || t("common.unknown")}`,
        `${t("settings.buildTime")}: ${state.buildTime ? new Date(state.buildTime).toLocaleString(locale) : t("common.unknown")}`,
        `${t("settings.swSupported")}: ${state.supported}`,
        `${t("settings.swRegistered")}: ${state.registered}`,
        `${t("settings.swActive")}: ${state.active}`,
        `${t("settings.swController")}: ${state.controller}`,
        `${t("settings.swUpdateAvailable")}: ${state.updateFound}`,
      ].join('\n')
      
      alert(info)
    } catch (error) {
      alert(t("settings.appInfoError"))
      console.error('App info error:', error)
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        onClick={handleQuickRepair}
        disabled={isRepairing}
        className={`h-9 px-3 ${QUIET}`}
        title={t("settings.quickRepairTitle")}
      >
        {isRepairing && repairType === 'quick' ? t("settings.repairing") : t("settings.quickRepair")}
      </button>
      <button
        onClick={handleFullRepair}
        disabled={isRepairing}
        className={`h-9 px-3 ${QUIET}`}
        title={t("settings.fullRepairTitle")}
      >
        {isRepairing && repairType === 'full' ? t("settings.repairing") : t("settings.fullRepair")}
      </button>
      <button
        onClick={showAppInfo}
        className={`h-9 px-3 ${QUIET}`}
        title={t("settings.appInfoTitle")}
      >
        {t("settings.appInfo")}
      </button>
      <a
        href="/manifest.webmanifest"
        target="_blank"
        rel="noreferrer"
        className={`h-9 px-3 ${QUIET}`}
      >
        {t("settings.viewManifest")}
      </a>
    </div>
  )
}

/* =============== Reusable bits =============== */
function InputRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mb-1 text-sm text-gray-600">{label}</div>
      {children}
    </label>
  )
}
function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mb-1 text-sm text-gray-600">{label}</div>
      {children}
    </label>
  )
}
function ToggleRow({
  label, description, checked, onChange, disabled = false,
}: {
  label: string
  description?: string
  checked: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
}) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-black/10 bg-white p-3">
      <div className="pr-3">
        <div className="text-sm font-medium text-gray-900">{label}</div>
        {description && <div className="text-xs text-gray-600">{description}</div>}
      </div>
      <label className={`relative inline-flex h-6 w-11 cursor-pointer items-center ${disabled ? "opacity-50" : ""}`}>
        <input
          type="checkbox"
          className="peer sr-only"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
        />
        {/* track */}
        <span className="h-6 w-11 rounded-full bg-gray-300 transition peer-checked:bg-[#1F3A93]/95" />
        {/* thumb */}
        <span className="pointer-events-none absolute left-0.5 top-0.5 size-5 rounded-full bg-white shadow transition peer-checked:translate-x-5" />
      </label>
    </div>
  )
}
function Toast({ children }: { children: React.ReactNode }) {
  return (
    <div className="fixed bottom-4 right-4 z-50 rounded-2xl bg-white/90 backdrop-blur-xl ring-1 ring-black/10 px-4 py-2 text-sm shadow-xl">
      {children}
    </div>
  )
}
function Skeleton() {
  return (
    <div className="animate-pulse rounded-3xl bg-white/90 backdrop-blur-xl ring-1 ring-black/10 shadow-lg p-5">
      <div className="h-4 w-1/3 rounded bg-gray-200" />
      <div className="mt-3 h-10 w-full rounded bg-gray-200" />
    </div>
  )
}

/* ---------- Renew Modal ---------- */
function RenewModal({
  renewsAt, priceUsd, onClose, onRenew, loading,
}: {
  renewsAt?: Date
  priceUsd: number
  onClose: () => void
  onRenew: (mode: "now" | "next") => void
  loading?: boolean
}) {
  const { t, language } = useLanguage()
  const locale = language === "es" ? "es-VE" : "en-US"
  const [mode, setMode] = React.useState<"now" | "next">("now")
  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-black/45 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-x-0 bottom-0 m-0 rounded-t-3xl bg-white/90 backdrop-blur-xl ring-1 ring-black/10 p-5 shadow-2xl sm:inset-auto sm:left-1/2 sm:top-1/2 sm:w-full sm:max-w-md sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-3xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-[16px] font-semibold text-gray-900">{t("settings.renewSubscription")}</h3>
          <button className={`h-8 px-3 ${QUIET}`} onClick={onClose}>{t("common.close")}</button>
        </div>
        <p className="text-sm text-gray-600">
          {t("settings.renewPlanFor", { price: priceUsd })} {renewsAt && <>{t("settings.currentRenewalDate")}: {renewsAt.toLocaleDateString(locale)}.</>}
        </p>

        <div className="mt-4 space-y-2">
          <label className="flex items-center gap-2 text-sm">
            <input type="radio" name="renew" checked={mode === "now"} onChange={() => setMode("now")} />
            {t("settings.renewNow")}
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="radio" name="renew" checked={mode === "next"} onChange={() => setMode("next")} />
            {t("settings.renewNextPeriod")}
          </label>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button className={`h-9 px-4 ${QUIET}`} onClick={onClose}>{t("settings.cancel")}</button>
          <button
            className="h-9 px-4 rounded-xl text-white shadow-lg hover:shadow-xl transition"
            style={{ background: "#111827" }}
            onClick={() => onRenew(mode)}
            disabled={loading}
          >
            {loading ? t("settings.processing") : t("settings.proceed")}
          </button>
        </div>
      </div>
    </div>
  )
}

/* ---------- App Version Section ---------- */
function AppVersionSection() {
  const { t, language } = useLanguage()
  const locale = language === "es" ? "es-VE" : "en-US"


  const [checking, setChecking] = React.useState(false)
  const [updating, setUpdating] = React.useState(false)
  const [updateAvailable, setUpdateAvailable] = React.useState(false)

  const checkForUpdates = async () => {
    setChecking(true)
    try {
      const state = await getServiceWorkerState()
      setUpdateAvailable(state.updateFound || false)
      
      if (state.updateFound) {
        toast.success(t("settings.updateAvailableToast"))
      } else {
        toast.success(t("settings.latestVersionToast"))
      }
    } catch (error) {
      console.error("Failed to check for updates:", error)
      toast.error(t("settings.checkUpdatesError"))
    } finally {
      setChecking(false)
    }
  }

  const handleUpdate = async () => {
    setUpdating(true)
    try {
      await forceUpdate()
      toast.success(t("settings.updatingApp"))
      // The page will reload automatically
    } catch (error) {
      console.error("Update failed:", error)
      toast.error(t("settings.updateError"))
      setUpdating(false)
    }
  }

  React.useEffect(() => {
    // Check on mount
    checkForUpdates()
  }, [])

  return (
    <div className="space-y-4">
      {/* Version Info */}
      <div className="rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 p-4 border border-blue-100">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="text-sm font-medium text-gray-700 mb-1">{t("settings.currentVersion")}</div>
            <div className="text-2xl font-bold text-gray-900">{__APP_VERSION__}</div>
            <div className="text-xs text-gray-600 mt-1">
              {t("settings.buildLabel")}: {new Date(__BUILD_TIME__).toLocaleString(locale)}
            </div>
            {updateAvailable && (
              <div className="mt-3 flex items-center gap-2 text-sm font-medium text-blue-600">
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {t("settings.newVersionAvailable")}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={checkForUpdates}
          disabled={checking}
          className={`h-9 px-4 ${QUIET} flex items-center gap-2`}
        >
          <svg className={`h-4 w-4 ${checking ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          {checking ? t("settings.checking") : t("settings.checkForUpdates")}
        </button>

        {updateAvailable && (
          <button
            onClick={handleUpdate}
            disabled={updating}
            className="h-9 px-4 rounded-xl text-white shadow-lg hover:shadow-xl transition flex items-center gap-2"
            style={{ background: "#1F3A93" }}
          >
            <svg className={`h-4 w-4 ${updating ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            {updating ? t("settings.updating") : t("settings.updateNow")}
          </button>
        )}
      </div>

      {/* Info Text */}
      <p className="text-xs text-gray-500">
        {t("settings.autoUpdateInfo")}
      </p>
    </div>
  )
}
